import { pushNotification } from '@frontend/pole-ui/lib/components/Notification'
import { push } from 'connected-react-router'
import { combineEpics } from 'redux-observable'
import { EMPTY, from, of, race, throwError } from 'rxjs'
import { catchError, filter, mergeMap, pairwise, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { isStatusOk } from '@/api/helpers'
import type { IResponse } from '@/api/interfaces'
import type { NAction } from '@/components/composed/Modal/actions'
import { closeModal, openModal } from '@/components/composed/Modal/actions'
import { getContentOptions, getModalOptions } from '@/components/composed/Modal/reducer'
import { EChoosingLocationRequestName } from '@/components/composed/modals/ChoosingLocationModal/constants'
import { getRequestData as getRequestApiData } from '@/logic/data/reducer'
import type { Epic } from '@/logic/interfaces'
import { pushSubmitModalHarvestGtmEvent } from '@/logic/metrika/harvest/helpers'
import { requestFinished } from '@/logic/requestStatus/actions'
import type { IRequestStatusPayload } from '@/logic/requestStatus/interfaces'
import { getRequestData } from '@/logic/requestStatus/reducer'
import type {
  IRequestHarvestSaleByTemplateFormValues,
  IRequestHarvestSaleByTemplateModalOptions,
  IRequestHarvestSaleDeliveryOptions,
} from '@/pages/HarvestSaleLots/components/RequestHarvestSaleByTemplateModal/interfaces'
import { pushClickToSubmitFormGtmEvent } from '@/pages/HarvestSaleLots/gtmHelpers'
import { getCreatingHarvestRequestBody, isErrorMultipleOrderWithBadRegion, transformPayloadStoreId } from '@/pages/HarvestSaleLots/helpers'
import type { TCreatingHarvestSaleRequestFinishedPayload } from '@/pages/HarvestSaleLots/interfaces'
import { getSuccessNotificationData } from '@/pages/Profiles/UserProfile/components/Sales/helpers'
import { HarvestSaleMultiRequestService, HarvestSaleRequestService } from '@/services/HarvestSaleRequestService'
import { EUserProfileRoute } from '@/types'
import { EOrderType } from '@/types/HarvestSaleLots'
import type { IStore } from '@/types/IStore'
import { getUrlWithQuery } from '@/utils/url'
import {
  clearSaleRequestBuyerOptions,
  clearSavedSaleRequestFormValues,
  requestHarvestSale,
  requestingHarvestSaleFailed,
  requestingHarvestSaleFinished,
} from './actions'
import type { NHarvestSaleAction } from './interfaces'
import { messages as harvestSaleMessages } from './messages'
import { getSavedSaleRequestForm } from './reducer'

type TSavedStoreMeta = IStore & { sourceFieldTag?: string; shouldSyncChosenAddress?: boolean }

const onRequestHarvestSaleDoMakeRequest: Epic<NHarvestSaleAction.IRequestHarvestSalePayload, typeof requestHarvestSale> = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(requestHarvestSale.match),
    switchMap(({ payload }) => {
      try {
        const { formValues, sourceOfOpening, template, userGtmInfo, userRegion } = payload
        const { orderType } = formValues
        const { value: state } = state$
        const stores = getRequestApiData<IStore[]>('stores')(state).data || []
        const storeId = stores.find(store => store.location.id === formValues.deliveryOptions.storeId)?.id

        const HarvestSaleRequestServices = {
          [EOrderType.solo]: new HarvestSaleRequestService(),
          [EOrderType.multiple]: new HarvestSaleMultiRequestService(),
        }

        // Сохраняем значение грузоотправителя из формы без автоматического подстановления склада
        const existingConsignor =
          payload.formValues.deliveryOptions?.consignor ??
          (payload.formValues as IRequestHarvestSaleByTemplateFormValues & { consignor?: string })?.consignor // запасной вариант для предыдущего расположения consignor

        const normalizedConsignor = typeof existingConsignor === 'string' ? existingConsignor.trim() : existingConsignor

        const resolvedConsignor = normalizedConsignor !== undefined && normalizedConsignor !== null ? normalizedConsignor : undefined

        const payloadWithConsignor: NHarvestSaleAction.IRequestHarvestSalePayload = {
          ...payload,
          formValues: {
            ...payload.formValues,
            deliveryOptions: {
              ...payload.formValues.deliveryOptions,
              consignor: resolvedConsignor,
              consignee: '',
            },
          },
        }

        const requestBody = getCreatingHarvestRequestBody(transformPayloadStoreId({ payload: payloadWithConsignor, storeId }))

        return from(HarvestSaleRequestServices[orderType].createHarvestSaleRequest(requestBody)).pipe(
          mergeMap(response => {
            if (!isStatusOk(response)) {
              return throwError(() => response)
            }

            const { data } = response

            const targetRequestID = orderType === EOrderType.multiple && Array.isArray(data) ? data.map(el => el.id) : undefined
            const requestID = orderType === EOrderType.solo && Array.isArray(data) ? data.map(el => el.id) : undefined

            if (userGtmInfo && userRegion) {
              pushSubmitModalHarvestGtmEvent({
                userGtmInfo,
                userRegionName: userRegion.name,
                eventLabel: 'step_4',
                template,
                sourceOfOpening,
                targetRequestID,
                requestID,
              })
            }

            const { title, description } = getSuccessNotificationData(orderType)

            // Возвращаем редирект
            return of(
              closeModal(),
              clearSavedSaleRequestFormValues(),
              clearSaleRequestBuyerOptions(),
              push(
                getUrlWithQuery(EUserProfileRoute.sales, {
                  shouldShowSuccessNotification: true,
                }),
              ),
              requestingHarvestSaleFinished({} as TCreatingHarvestSaleRequestFinishedPayload),
              pushNotification({
                status: 'success',
                title,
                description,
              }),
            )
          }),
          catchError((response: IResponse<undefined>) => {
            const { meta } = response
            pushClickToSubmitFormGtmEvent(meta?.message ?? 'error')

            return of(
              requestingHarvestSaleFailed(response),
              orderType === EOrderType.multiple && isErrorMultipleOrderWithBadRegion(response)
                ? openModal({
                    options: {
                      dialogId: 'FailureOrderModal',
                    },
                    contentOptions: {
                      titleId: 'failureOrderModal.leadMultipleOrdered.title',
                      messageId: 'failureOrderModal.leadMultipleOrdered.text',
                    },
                  })
                : null,
            )
          }),
        )
      } catch (error) {
        const rawMessage = error instanceof Error ? error.message : ''

        const errorMessage =
          rawMessage === 'INVALID_DEPARTURE_DATE_RANGE'
            ? harvestSaleMessages.errors.invalidDepartureDate
            : harvestSaleMessages.errors.requestCreation

        const failurePayload = {
          meta: {
            status: 'ERROR',
            message: errorMessage,
          },
        } as IResponse<undefined>

        pushClickToSubmitFormGtmEvent(errorMessage)

        return of(requestingHarvestSaleFailed(failurePayload), pushNotification({ status: 'error', title: errorMessage }))
      }
    }),
    tap(() => requestingHarvestSaleFinished({} as TCreatingHarvestSaleRequestFinishedPayload)),
  )

const onOpenModalDoListenForClosingOfAddingAddressModal: Epic<NAction.IOpenModalPayload, typeof openModal> = (action$, state$) =>
  action$.pipe(
    filter(openModal.match),
    pairwise(),
    mergeMap(([lastAction, currentAction]) => {
      const wasParent = lastAction.payload.options.dialogId === 'RequestHarvestSaleByTemplateModal'
      const isChild = currentAction.payload.options.dialogId === 'ChoosingLocationModal'
      const shouldRestoreParent = Boolean(currentAction.payload.contentOptions?.restoreParentModalOnSuccess)

      if (wasParent && isChild && currentAction.payload.contentOptions?.position === 'modal' && shouldRestoreParent) {
        return race(
          action$.pipe(
            filter(closeModal.match),
            withLatestFrom(state$),
            take(1),
            mergeMap(([, state]) => {
              const { values, template, harvest, isOwnOffer, draft } = getSavedSaleRequestForm()(state)
              const addingAddressRequestResponse = getRequestData<IStore>(EChoosingLocationRequestName.SAVE)(state)
              const { id, location } = addingAddressRequestResponse || {}
              const { deliveryOptions } = values || {}

              if (!values) {
                return from([clearSavedSaleRequestFormValues(), clearSaleRequestBuyerOptions()])
              }

              return from(
                [
                  openModal({
                    options: {
                      dialogId: 'RequestHarvestSaleByTemplateModal',
                    },
                    contentOptions: {
                      customInitialFormValues: {
                        ...values,
                        deliveryOptions: {
                          ...deliveryOptions,
                          storeId: id,
                        },
                        regionCode: location?.region.code,
                      },
                      draft,
                      template,
                      harvest,
                      isOwnOffer,
                      defaultShownStep: 1,
                    } as IRequestHarvestSaleByTemplateModalOptions,
                  }),
                  clearSavedSaleRequestFormValues(),
                  clearSaleRequestBuyerOptions(),
                ].filter(Boolean),
              )
            }),
          ),
        )
      }

      return EMPTY
    }),
  )

const onChoosingLocationSaveFinished: Epic<IRequestStatusPayload, typeof requestFinished> = (action$, state$) =>
  action$.pipe(
    filter(requestFinished.match),
    filter(({ payload }) => payload.name === EChoosingLocationRequestName.SAVE),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const modalOptions = getModalOptions()(state)
      const contentOptions = getContentOptions()(state)

      const shouldRestoreParent = Boolean(contentOptions?.restoreParentModalOnSuccess)
      const isChoosingLocationOpen = modalOptions.dialogId === 'ChoosingLocationModal'

      if (!shouldRestoreParent || !isChoosingLocationOpen) {
        return EMPTY
      }

      const { values, template, harvest, isOwnOffer, draft } = getSavedSaleRequestForm()(state)

      if (!values) {
        return of(clearSavedSaleRequestFormValues(), clearSaleRequestBuyerOptions())
      }

      const addingAddressRequestResponse = getRequestData<TSavedStoreMeta>(EChoosingLocationRequestName.SAVE)(state)
      const { id, location } = addingAddressRequestResponse || {}
      const deliveryOptions = values.deliveryOptions ?? ({} as IRequestHarvestSaleDeliveryOptions)

      const sourceFieldTag = addingAddressRequestResponse?.sourceFieldTag

      const restoredDeliveryOptions =
        sourceFieldTag && sourceFieldTag.includes('consignor')
          ? deliveryOptions
          : {
              ...deliveryOptions,
              storeId: id || deliveryOptions.storeId,
            }

      const restoredRegionCode = location?.region?.code ?? values.regionCode

      return of(
        openModal({
          options: {
            dialogId: 'RequestHarvestSaleByTemplateModal',
          },
          contentOptions: {
            customInitialFormValues: {
              ...values,
              deliveryOptions: restoredDeliveryOptions,
              regionCode: restoredRegionCode,
            },
            draft,
            template,
            harvest,
            isOwnOffer,
            defaultShownStep: 1,
          } as IRequestHarvestSaleByTemplateModalOptions,
        }),
        clearSavedSaleRequestFormValues(),
        clearSaleRequestBuyerOptions(),
      )
    }),
  )
//eslint-disable-next-line @typescript-eslint/no-explicit-any
export default (<any>combineEpics)(
  onRequestHarvestSaleDoMakeRequest,
  onOpenModalDoListenForClosingOfAddingAddressModal,
  onChoosingLocationSaveFinished,
)
